"use client";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { extractTrackFromPathname } from "@/lib/roles";
import { useAuth } from "@/context/auth-context";
import { useAuthStore } from "@/store/auth-store";
import { useEffect, useState } from "react";
import { UserButton, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { DotIcon } from "lucide-react";

interface NavItem {
  href: string;
  label: string;
  external?: boolean;
  highlight?: boolean;
}

// Route group (public) should not appear in URLs. Use clean public-facing paths.
// Split navigation: primary pages (center), utility pages (optional), and action (login)
const primaryNav: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/begin", label: "Begin" },
  { href: "/rotations", label: "Rotations" },
  { href: "/research", label: "Research" },
  { href: "/testimonials", label: "Testimonials" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
  { href: "/blog", label: "Blog" },
];

const secondaryNav: NavItem[] = [
  { href: "/about", label: "About" },
  { href: "/pricing", label: "Pricing" },
];

const actionNav: NavItem = {
  href: "/auth/login",
  label: "Login",
  highlight: true,
};

export default function SiteHeader() {
  const pathname = usePathname();
  const currentTrack = extractTrackFromPathname(pathname);

  // Prefer store for consistency (falls back to context if present)
  const storeUser = useAuthStore((s) => s.user);
  const { user: ctxUser } = useAuth();
  const user = storeUser || ctxUser;
  
  // Also check Clerk's auth state directly
  const { isSignedIn, isLoaded: isClerkLoaded } = useClerkAuth();
  
  // User is authenticated if either custom auth has user OR Clerk says signed in
  const isAuthenticated = Boolean(user) || (isClerkLoaded && isSignedIn);

  // Hydration fix: track if component is mounted
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Role-based destination
  const destinationForRole = (() => {
    if (!user) return null;
    if (user.role === "preceptor") return "/preceptor/dashboard";
    if (user.role === "student") return "/student/profile";
    if (user.role === "admin") return "/admin";
    if (user.role === "staff") return "/staff";
    return "/";
  })();

  return (
    <header className="border-b sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-[var(--brand-border)] bg-white/85">
      {/* Full-width row; remove horizontal padding so logo sits flush left */}
      <div className="h-16 flex items-center gap-4 pr-6 md:pr-8 pl-3 md:pl-4 w-full">
        {/* Brand */}
        <Link
          href="/"
          className="flex items-center gap-2"
          aria-label="ACE Rotations Home"
        >
          <Image
            src="/images/Ace-Main-Logo.png"
            alt="ACE Rotations"
            width={140}
            height={100}
            className="select-none object-contain h-10 w-auto"
            priority
            quality={100}
          />
          {/* Site title — shown on non-mobile sizes only, gradient text */}
          <span className="hidden sm:inline text-lg font-bold whitespace-nowrap bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
            ACE Rotations
          </span>
        </Link>

        {/* Center Navigation / Hidden when authenticated */}
        <div className="flex-1 flex justify-center px-2">
          {isMounted && !isAuthenticated && (
            <nav className="hidden lg:flex items-center gap-6 text-sm">
              {primaryNav.map((i) => {
                const active =
                  pathname === i.href || pathname.startsWith(i.href + "/");
                return (
                  <Link
                    key={i.href}
                    href={i.href}
                    className={[
                      "transition-colors",
                      active
                        ? "text-[var(--brand-primary)] font-medium"
                        : "text-[var(--brand-muted)] hover:text-[var(--brand-primary)]",
                    ].join(" ")}
                  >
                    {i.label}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        {/* Secondary Nav (hidden when authenticated) */}
        {isMounted && !isAuthenticated && (
          <nav className="hidden xl:flex items-center gap-4 text-xs">
            {secondaryNav.map((i) => {
              const active =
                pathname === i.href || pathname.startsWith(i.href + "/");
              return (
                <Link
                  key={i.href}
                  href={i.href}
                  className={[
                    "transition-colors",
                    active
                      ? "text-[var(--brand-primary)] font-medium"
                      : "text-[var(--brand-muted)] hover:text-[var(--brand-primary)]",
                  ].join(" ")}
                >
                  {i.label}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Action Area */}
        <div className="flex items-center gap-3">
          {isMounted && !isAuthenticated && (
            <>
              <Link
                href={
                  currentTrack
                    ? `/preceptor/${currentTrack}/login`
                    : "/auth/preceptor/login"
                }
                className="text-xs px-2 py-1.5 rounded-md border border-[var(--brand-border)] bg-white hover:bg-[var(--brand-muted-bg)] transition-colors text-[var(--brand-muted)] hover:text-[var(--brand-primary)]"
              >
                {currentTrack
                  ? `${currentTrack.toUpperCase()} Preceptor`
                  : "Preceptor Portal"}
              </Link>
              <Link
                href={
                  currentTrack
                    ? `/student/${currentTrack}/login`
                    : "/auth/student/login"
                }
                className={[
                  "text-sm px-3 py-1.5 rounded-md border border-[var(--brand-border)] hover:bg-[var(--brand-muted-bg)] transition-colors",
                  pathname.startsWith(actionNav.href)
                    ? "text-[var(--brand-primary)] font-medium"
                    : "text-[var(--brand-muted)]",
                ].join(" ")}
              >
                {actionNav.label}
              </Link>
            </>
          )}
          {isMounted && isAuthenticated && (
            <div className="flex items-center gap-2">
              {destinationForRole && (
                <Link
                  href={destinationForRole}
                  className="text-xs md:text-sm px-3 py-1.5 rounded-md border border-[var(--brand-border)] bg-white hover:bg-[var(--brand-muted-bg)] transition-colors text-[var(--brand-muted)] hover:text-[var(--brand-primary)]"
                >
                  {user?.role === "preceptor"
                    ? user?.status === "pending"
                      ? "Complete Profile"
                      : "Dashboard"
                    : "Portal"}
                </Link>
              )}
              {user?.role === "preceptor" && user?.status === "pending" && (
                <span className="text-[10px] font-medium rounded-full bg-amber-100 text-amber-700 px-2 py-1">
                  Pending
                </span>
              )}
              <UserButton showName>
                {/* You can pass the content as a component */}
                {/* <UserButton.UserProfilePage
                  label="Custom Page"
                  url="custom"
                  labelIcon={<DotIcon />}
                >
                  <div>Hi</div>
                </UserButton.UserProfilePage> */}

                {/* You can also pass the content as direct children */}
                <UserButton.UserProfilePage
                  label="Terms"
                  labelIcon={<DotIcon />}
                  url="terms"
                >
                  <div>
                    <h1>Custom Terms Page</h1>
                    <p>This is the content of the custom terms page.</p>
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>

              {/* <SignOutButton>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-[var(--brand-border)] bg-white hover:bg-[var(--brand-muted-bg)] text-[var(--brand-muted)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </SignOutButton> */}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
